import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';
import { navbarConfig } from '../config/navbarConfig';
import { useAnchorScroll } from '../hooks/useAnchorScroll';

/**
 * MenuItem component - Renders a single menu item with optional submenu
 */
function MenuItem({ item, isActive, scrollToAnchor }) {
  const [isHovered, setIsHovered] = useState(false);
  const hasSubmenu = item.submenu && item.submenu.length > 0;

  const handleMenuClick = () => {
    // Always scroll to top when clicking a menu item
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        '&::after': isHovered && hasSubmenu ? {
          content: '""',
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          height: '4px',
          zIndex: 1000,
        } : {},
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        component={Link}
        to={item.to}
        onClick={handleMenuClick}
        sx={{
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: '#4a6a4a',
          transition: 'opacity 0.2s ease',
          '&:hover': {
            opacity: 0.7,
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: isActive ? 700 : 600,
            fontSize: { xs: '0.77rem', sm: '0.89rem' },
            color: '#4a6a4a',
            mb: 0.1,
          }}
        >
          {item.label.toUpperCase()}
        </Typography>
      </Box>

      {/* Submenu */}
      {hasSubmenu && isHovered && (
        <Box
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            position: 'absolute',
            top: 'calc(100% + 2px)',
            left: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 1,
            border: '1px solid rgba(74, 106, 74, 0.1)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            minWidth: '200px',
            py: 0.5,
            zIndex: 1001,
            mt: 0,
          }}
        >
          {item.submenu.map((subItem, index) => (
            <Box
              key={index}
              component="a"
              href={`#${subItem.anchorId}`}
              onClick={subItem.onClick || scrollToAnchor(subItem.anchorId, subItem.anchorPage)}
              sx={{
                display: 'block',
                textDecoration: 'none',
                px: 2,
                py: 1,
                color: '#4a6a4a',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(74, 106, 74, 0.05)',
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.77rem', sm: '0.89rem' },
                  color: '#4a6a4a',
                }}
              >
                {subItem.label.toUpperCase()}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollToAnchor = useAnchorScroll();
  const isActive = (to) => location.pathname === to;

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Si ya estamos en el inicio, solo hacer scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Si estamos en otra página, navegar y luego hacer scroll
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(74, 106, 74, 0.1)',
        py: { xs: 1.5, sm: 2 },
        transition: 'all 0.3s ease',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            // Logo left, menu items right
            justifyContent: 'space-between',
            gap: { xs: 3, sm: 5 },
          }}
        >
          {/* Logo */}
          <Box
            component="button"
            onClick={handleLogoClick}
            sx={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              transition: 'opacity 0.2s ease',
              '&:hover': {
                opacity: 0.7,
              },
            }}
          >
            <Box
              component="img"
              src="/huelmu_h.svg"
              alt="Huelmu"
              sx={{
                // ~15% smaller, keep aspect ratio (width stays auto)
                height: { xs: '27px', sm: '31px', md: '34px' },
                width: 'auto',
                objectFit: 'contain',
                maxWidth: { xs: '102px', sm: '119px', md: '136px' },
              }}
            />
          </Box>
          
          {/* Menu items (right) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 2, sm: 3 },
            }}
          >
            {navbarConfig.map((item, index) => (
              <MenuItem
                key={index}
                item={item}
                isActive={isActive(item.to)}
                scrollToAnchor={scrollToAnchor}
              />
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Navbar;

